import { Attribute, WorkflowCondition } from "lib/api";
import { formatToUTC, validDate, validTime } from "lib/utils/datetime";
import { Query, QueryGroup, QueryRuleValue } from "./types";

export const locationAttribute = {
  type: "distance",
  label: "Distance",
  appearance: {},
};

const formatToUTCAsPossible = (
  date: string | undefined,
  time: string | undefined,
  timezone: string
) => {
  if (validDate(date) && validTime(time)) {
    return formatToUTC(`${date} ${time}`, timezone);
  }

  return `${date} ${time}`;
};

const ruleValueToConditionValue = (
  value: QueryRuleValue | undefined,
  timezone: string
): Pick<WorkflowCondition, "value" | "rangevalues"> => {
  if (typeof value === "object") {
    if ("distance" in value) {
      return {
        value: value.distance,
        rangevalues: [
          "Coordinates",
          value.latitude?.toString() ?? "",
          value.longitude?.toString() ?? "",
          value.address ?? "",
        ],
      };
    }

    if ("from" in value) {
      return {
        rangevalues: [value.from?.toString() ?? "", value.to?.toString() ?? ""],
      };
    }

    if ("start" in value) {
      return {
        rangevalues: [
          formatToUTCAsPossible(value.start?.date, value.start?.time, timezone),
          formatToUTCAsPossible(value.end?.date, value.end?.time, timezone),
        ],
      };
    }

    if ("date" in value) {
      return {
        value: formatToUTCAsPossible(value.date, value.time, timezone),
      };
    }
  }

  return { value };
};

const queryGroupToWorkflowCondition = (
  attributes: Record<string, Attribute>,
  group: QueryGroup,
  groupId = 0,
  timezone: string
): Array<WorkflowCondition> => {
  const conditions: Array<WorkflowCondition> = [];

  let groupConter = groupId;
  group.rules.forEach((rule) => {
    if ("combinator" in rule) {
      conditions.push(
        ...queryGroupToWorkflowCondition(
          attributes,
          rule,
          // eslint-disable-next-line no-plusplus
          ++groupConter,
          timezone
        )
      );
    } else {
      const isLocation = rule.attribute === "location";
      const ruleAttribute = rule?.attribute;
      const attribute = isLocation
        ? locationAttribute
        : ruleAttribute
        ? attributes[ruleAttribute]
        : undefined;
      conditions.push({
        groupId,
        groupLogic: group.combinator,

        property: rule.attribute,
        operator: isLocation ? "distance" : rule.operator,
        ...ruleValueToConditionValue(rule.value, timezone),

        type: attribute?.type,
        displayLabel: attribute?.label,
      });
    }
  });

  return conditions;
};

export const queryToWorkflowCondition = (
  query: Query,
  attributes: Record<string, Attribute> = {},
  timezone: string
): Array<WorkflowCondition> => {
  return queryGroupToWorkflowCondition(attributes, query, 0, timezone);
};
